import {BaseElement, OSkinableInit} from "/@back@/commons/basis.js";
import {CellBuilderString, GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {GridSmall, OGridSmallInit} from "/@back@/commons/widgets/grid-tags.js";
import {GridColTreeDef, GridDataHolderJsonTree} from "/@back@/commons/widgets/tree.js";
import {IReg, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IWspEnv} from "/@lib@/wsp/wsp.js";
import {JSrcFields, JSubItem} from "/@lib@/wsp/src.js";
import {LANG} from "/@lib@/commons/lang.js";
import {ITEM} from "/@lib@/wsp/item.js";
import {EItemTypeFamily, WspMetaUi} from "/@lib@/wsp/wspMetaUi.js";

/**
 *
 */
export interface SubItemsTree extends BaseElement {
	initialize(init?: OSubItemsTreeInit): this
}

export interface OSubItemsTreeInit extends OSkinableInit {
	reg?: IReg<IWspEnv & IUiEnv>

	/** Item à afficher. Doit avoir le field itSubItems renseigné. */
	item?: JSrcFields

	/** Si null ou undefined, pas de champs de filtrage de l'arbre affiché, "" pour afficher le champs vide. */
	textFilter?: null | string

	/** Signatures des subItems recherchées ou à highlighter. */
	subItemSgnPattern?: RegExp

	/**
	 * L'item racine est-il affiché en noeud racine de l'arbre.
	 * Si non renseigné, l'item sera affiché si sa signature match OSubItemsTreeInit.subItemSgnPattern.
	 */
	showItem?: boolean

	/** Préserve l'affichage de la ou les lignes sélectionnées même si le filtre ne matche pas. */
	forbidHideSelectedRow?: boolean;

	grid?: OGridSmallInit
}

export class SubItemsTree extends BaseElement {

	reg: IReg<IWspEnv & IUiEnv>;

	/** L'item racine est-il affiché. */
	itemShown: boolean;

	item: JSrcFields;

	subModelPattern?: RegExp

	protected forbidHideSelRow: boolean;

	dataHolder: GridDataHolderJsonTree<ISubItemTree>;

	grid: GridSmall;
	search: HTMLInputElement;

	protected datas: ISubItemTree[];

	/**
	 * @param showItem Si non renseigné, l'item sera affiché si sa signature match OSubItemsTreeInit.subItemSgnPattern.
	 */
	setSubItems(item: JSrcFields | null, showItem?: boolean) {
		this.item = item;
		if (!item) {
			this.datas = [];
		} else {
			this.itemShown = showItem;
			if (this.itemShown == null) {
				this.itemShown = this.subModelPattern?.test(item.itModel) || false;
			}
			if (this.itemShown) {
				this.datas = [{
					id: null, // Racine de l'item
					ti: item.itTi || ITEM.extractItemCode(item.srcUri),
					mo: item.itModel,
					subItems: item.itSubItems || []
				}];
			} else {
				this.datas = item.itSubItems || [];
			}
		}
		this.redrawDatas(false);
	}

	getSelectedSubItem(): ISubItemTree | null {
		const row = this.dataHolder.getRow(this.grid.getSelectedRow());
		if (!row) return null;
		return row.rowDatas;
	}

	getSelectedSubItems(): ISubItemTree[] {
		return this.dataHolder.getSelectedDatas();
	}

	selectFirst(): boolean {
		for (let i = 0, c = this.dataHolder.countRows(); i < c; i++) {
			const subModel = this.dataHolder.getRow(i).rowDatas.mo;
			if (!this.subModelPattern || subModel?.match(this.subModelPattern)) {
				this.grid.setSelectedRows(i);
				return true;
			}
		}
		return false;
	}

	selectBySubItems(subItems: ISubItemTree[]): void {
		if (subItems) {
			const sel: number[] = [];
			for (let i = 0, c = this.dataHolder.countRows(); i < c; i++) {
				const subIt = this.dataHolder.getRow(i).rowDatas;
				if (subItems.find((entry: ISubItemTree) => entry.id === subIt.id)) sel.push(i);
			}
			this.grid.setSelectedRows(sel);
		} else {
			this.grid.setSelectedRows(-1);
		}
	}

	protected _initialize(init: OSubItemsTreeInit) {
		this.reg = this.findReg(init);

		this.subModelPattern = init.subItemSgnPattern;

		this.dataHolder = new GridDataHolderJsonTree<ISubItemTree>('subItems')
			.setDefaultOpenState(GridDataHolderJsonTree.defaultOpened);

		const colDefs = [new GridColTreeDef('tree')
			.setFlex('10rem', 1, 1).setMinWidth('55px')
			.setCellBuilder(new CellBuilderSubItem(this.reg.env.wsp.wspMetaUi))];

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		if (init.textFilter != null) {
			//On ajoute le filtre
			if (init.forbidHideSelectedRow) this.forbidHideSelRow = true;
			this.search = sr.appendChild(<input type="search" spellcheck="false" oninput={this.onSearchInput} onkeydown={this.onKeyDown}/>) as HTMLInputElement;
			this.search.value = init.textFilter;
		}
		this.grid = sr.appendChild(new GridSmall().initialize(Object.assign({
			columnDefs: colDefs,
			dataHolder: this.dataHolder,
			hideHeaders: true
		}, init.grid)));

		this.setSubItems(init.item, init.showItem);
	}

	clearSearch(): void {
		if (this.search?.value != "") {
			this.search.value = "";
			this.search.dispatchEvent(new CustomEvent('input'));
		}
	}

	protected onSearchInput(this: HTMLInputElement, ev: InputEvent) {
		DOMSH.findHost<SubItemsTree>(this).redrawDatas(true);
	}

	protected onKeyDown(this: HTMLInputElement, ev: KeyboardEvent) {
		if (ev.key === 'ArrowDown') {
			const tree = DOMSH.findHost(this) as SubItemsTree;
			if (tree.grid.dataHolder.countRows() > 0) {
				tree.grid.focus();
				if (tree.grid.getSelectedRow() === undefined) tree.grid.setSelectedRows(0);
			}
		}
	}

	protected redrawDatas(preserveSel: boolean) {
		const selected: ISubItemTree[] = preserveSel ? this.getSelectedSubItems() : null;
		const filter = this.search?.value;
		if (filter) {
			const regexp = new RegExp(LANG.escape4RegexpFuzzy(filter), "i");
			const buildFilteredDatas = (list: ISubItemTree[]): ISubItemTree[] => {
				const r: ISubItemTree[] = [];
				for (const e of list) {
					if ((this.forbidHideSelRow && selected?.indexOf(e) > -1) || e.ti?.match(regexp)) {
						const newE = Object.create(e) as ISubItemTree;
						if (e.subItems) newE.subItems = buildFilteredDatas(e.subItems);
						r.push(newE);
					} else if (e.subItems) {
						const ch = buildFilteredDatas(e.subItems);
						if (ch.length > 0) {
							const newE = Object.create(e) as ISubItemTree;
							newE.subItems = ch;
							r.push(newE);
							continue;
						}
					}
				}
				return r;
			};
			this.dataHolder.setDatas(buildFilteredDatas(this.datas));
		} else {
			this.dataHolder.setDatas(this.datas);
		}
		this.selectBySubItems(selected);
		// if (this.grid.getSelectedRow() === undefined) {
		// 	//Si la sel a disparu, on reselectionne le 1er subItem.
		// 	this.selectFirst();
		// }
	}
}

REG.reg.registerSkin('wsp-subitems-tree', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	input {
		border: none;
		color: var(--form-color);
		border-bottom: 1px solid var(--border-color);
		background: .1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg) var(--form-search-bgcolor);
		padding-block: 2px;
		padding-inline: 1.2em 2px;
	}

	input:focus {
		outline: var(--focus-outline);
	}

	c-grid-small {
		flex: 1;
		border: none;
		background-color: var(--row-bgcolor);
	}
`);

customElements.define("wsp-subitems-tree", SubItemsTree);


export interface ISubItemTree extends JSubItem {
	/** null = item racine. */
	id: string | null
}

class CellBuilderSubItem extends CellBuilderString {
	constructor(public wspMetaUi: WspMetaUi) {super("ti")}

	protected _getValue(row: GridDataRowJson<ISubItemTree>): any {
		let v = row.rowDatas.ti;
		if (v) return v;
		const subItemType = this.wspMetaUi.getItemType(row.rowDatas.mo);
		if (subItemType && subItemType.getFamily() !== EItemTypeFamily.undef) return subItemType.getTitle() + " [" + row.rowDatas.id + "]";
		return row.rowDatas.id;
	}
}